import type { AutoGeneratorForFormModel } from "@/components/features/_model";

/**
 *
 * @description InputModel の Node までの path を生成する再帰関数
 *
 */
const generateNodePathsHelper = (
	_obj: unknown,
	currentPath: Array<string | number> = [],
): Array<Array<string | number>> => {
	let paths: Array<Array<string | number>> = [];

	if (typeof _obj !== "object" || _obj === null) {
		// 末端の Node まで到達したので、現在のパスを返す
		return [currentPath];
	}

	for (const key in _obj) {
		// 配列の index は number であるため、型を変換する
		const keyType = Array.isArray(_obj) ? Number(key) : key;
		// キーを現在のパスに追加
		const newPath = currentPath.concat(keyType);

		// オブジェクトの値がさらにオブジェクトである場合は、再帰的に処理
		if (typeof _obj[key as keyof typeof _obj] === "object" && _obj[key as keyof typeof _obj] !== null) {
			const nextObj = _obj[key as keyof typeof _obj];
			const childPaths = generateNodePathsHelper(nextObj, newPath); // 再帰
			paths = paths.concat(childPaths);
		} else {
			// 末端の Node であれば、そのパスを追加して終了
			paths.push(newPath);
		}
	}

	return paths;
};

/* eslint-disable @typescript-eslint/no-namespace */
export namespace InputModelPathUtil {
	/**
	 *
	 * @description InputModelを 受け取って、末端の Node の path の多次元配列を返す
	 *
	 * @example
	 * ```
	 * ```
	 *
	 * @param inputModel
	 * @returns [Array<string | number>]
	 */
	export const generateNodePaths = <S, I, E>(
		_inputModel: AutoGeneratorForFormModel<S, I, E>["InputModel"],
	): Array<Array<string | number>> => {
		return generateNodePathsHelper(_inputModel);
	};

	/**
	 *
	 * @description path を受け取って、Primitive 型の値を返す
	 *
	 * @example
	 * ```
	 * ```
	 *
	 * @param _path
	 * @param _viewModel
	 * @returns string | number | boolean | bigint | null | undefined
	 */
	export const getObjectWithPath: <S, I, E>(
		path: Array<string | number>,
		inputModel: AutoGeneratorForFormModel<S, I, E>["InputModel"],
	) => unknown = (path, inputModel) => {
		try {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const parsed = inputModel as { [key: string | number]: any };
			// ViewModel がジェネリクスで型が定義されているので、返却値は as で型を指定する
			return path.reduce((acc, key) => acc[key], parsed) as unknown;
		} catch {
			// 例外を undefined として丸める
			return undefined;
		}
	};
}
